import type { JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

interface CategoryPromoItem {
    id: number;
    name: string;
    slug: string;
    url: string;
}

interface CategoryMarqueeProps {
    categories: CategoryPromoItem[];
    title?: string;
}

interface CategoryMarqueeRow {
    categories: CategoryPromoItem[];
    direction: 'forward' | 'reverse';
}

type MarqueeRowDirection = CategoryMarqueeRow['direction'];
type MarqueeRowInteraction = 'left' | 'right' | 'pause' | null;
type MarqueePointerState = Record<MarqueeRowDirection, MarqueeRowInteraction>;
type MarqueePointerPosition = {
    x: number;
    y: number;
};
type MarqueeRowMotion = {
    offset: number;
    cycleWidth: number;
};

const defaultPointerState: MarqueePointerState = {
    forward: null,
    reverse: null,
};
const baseSpeed: Record<MarqueeRowDirection, number> = {
    forward: 7,
    reverse: -6.5,
};
const edgeStartSpeed = 42;
const edgeMaxSpeed = 128;
const edgeRampDuration = 2400;

export function getEdgeBoostSpeed(elapsedMilliseconds: number): number {
    const progress = Math.min(Math.max(elapsedMilliseconds, 0) / edgeRampDuration, 1);
    const easedProgress = 1 - Math.pow(1 - progress, 3);

    return edgeStartSpeed + (edgeMaxSpeed - edgeStartSpeed) * easedProgress;
}

export function createCategoryRows(categories: CategoryPromoItem[]): CategoryMarqueeRow[] {
    const firstRow = categories.filter((_, index) => index % 2 === 0);
    const secondRow = categories.filter((_, index) => index % 2 === 1);

    return [
        {
            categories: firstRow.length > 0 ? firstRow : categories,
            direction: 'forward',
        },
        {
            categories: secondRow.length > 0 ? secondRow : categories,
            direction: 'reverse',
        },
    ];
}

export function getRepeatCount(categories: CategoryPromoItem[]): number {
    if (categories.length <= 2) {
        return 10;
    }

    if (categories.length <= 5) {
        return 8;
    }

    return 4;
}

function CategoryPill({
    category,
    inert = false,
}: {
    category: CategoryPromoItem;
    inert?: boolean;
}) {
    return (
        <a
            href={category.url}
            class="category-marquee__pill"
            aria-hidden={inert ? 'true' : undefined}
            tabIndex={inert ? -1 : undefined}
        >
            {category.name}
        </a>
    );
}

export default function CategoryMarquee({
    categories,
    title = 'Utforska efter kategori',
}: CategoryMarqueeProps) {
    const pointerStateRef = useRef<MarqueePointerState>(defaultPointerState);
    const pointerPositionRef = useRef<MarqueePointerPosition | null>(null);
    const monitorTimerRef = useRef<number | null>(null);
    const frameRef = useRef<number | null>(null);
    const lastFrameTimeRef = useRef<number | null>(null);
    const pointerStateStartedAtRef = useRef<Record<MarqueeRowDirection, number>>({
        forward: 0,
        reverse: 0,
    });
    const rowMotionRef = useRef<Record<MarqueeRowDirection, MarqueeRowMotion>>({
        forward: {
            offset: 0,
            cycleWidth: 0,
        },
        reverse: {
            offset: 0,
            cycleWidth: 0,
        },
    });
    const sectionRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        return () => {
            if (monitorTimerRef.current !== null) {
                window.clearInterval(monitorTimerRef.current);
                monitorTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const section = sectionRef.current;

        if (!section || categories.length === 0) {
            return;
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        function measureRows(): void {
            section.querySelectorAll<HTMLElement>('.category-marquee__row').forEach((row) => {
                const direction = getRowDirection(row);
                const firstSet = row.querySelector<HTMLElement>('.category-marquee__set');

                if (!firstSet) {
                    return;
                }

                const rowStyles = window.getComputedStyle(row);
                const gap = Number.parseFloat(rowStyles.columnGap || rowStyles.gap || '0');
                const cycleWidth = firstSet.getBoundingClientRect().width + gap;
                const motion = rowMotionRef.current[direction];

                motion.cycleWidth = cycleWidth;

                if (motion.offset === 0 && direction === 'forward') {
                    motion.offset = -cycleWidth;
                }
            });
        }

        function getVelocity(direction: MarqueeRowDirection, timestamp: number): number {
            const interaction = pointerStateRef.current[direction];

            if (interaction === 'pause') {
                return 0;
            }

            if (interaction === 'left') {
                return getEdgeBoostSpeed(timestamp - pointerStateStartedAtRef.current[direction]);
            }

            if (interaction === 'right') {
                return -getEdgeBoostSpeed(timestamp - pointerStateStartedAtRef.current[direction]);
            }

            return baseSpeed[direction];
        }

        function animateRow(row: HTMLElement, deltaSeconds: number, timestamp: number): void {
            const direction = getRowDirection(row);
            const motion = rowMotionRef.current[direction];

            if (motion.cycleWidth <= 0) {
                return;
            }

            motion.offset += getVelocity(direction, timestamp) * deltaSeconds;

            while (motion.offset >= 0) {
                motion.offset -= motion.cycleWidth;
            }

            while (motion.offset <= -motion.cycleWidth) {
                motion.offset += motion.cycleWidth;
            }

            row.style.transform = `translate3d(${motion.offset}px, 0, 0)`;
        }

        function animate(timestamp: number): void {
            const lastTimestamp = lastFrameTimeRef.current ?? timestamp;
            const deltaSeconds = Math.min((timestamp - lastTimestamp) / 1000, 0.05);

            lastFrameTimeRef.current = timestamp;
            section.querySelectorAll<HTMLElement>('.category-marquee__row').forEach((row) => {
                animateRow(row, deltaSeconds, timestamp);
            });
            frameRef.current = window.requestAnimationFrame(animate);
        }

        measureRows();

        const resizeObserver = new ResizeObserver(measureRows);

        section.querySelectorAll<HTMLElement>('.category-marquee__row').forEach((row) => {
            resizeObserver.observe(row);
        });

        frameRef.current = window.requestAnimationFrame(animate);

        return () => {
            resizeObserver.disconnect();

            if (frameRef.current !== null) {
                window.cancelAnimationFrame(frameRef.current);
                frameRef.current = null;
            }

            lastFrameTimeRef.current = null;
        };
    }, [categories.length]);

    function updatePointerState(state: MarqueePointerState = defaultPointerState): void {
        const nextState = {
            forward: state.forward,
            reverse: state.reverse,
        };
        const now = performance.now();

        (['forward', 'reverse'] as const).forEach((direction) => {
            if (pointerStateRef.current[direction] !== nextState[direction]) {
                pointerStateStartedAtRef.current[direction] = now;
            }
        });

        pointerStateRef.current = nextState;
    }

    function isSamePointerState(
        currentState: MarqueePointerState,
        nextState: MarqueePointerState,
    ): boolean {
        return (
            currentState.forward === nextState.forward && currentState.reverse === nextState.reverse
        );
    }

    function queuePointerState(state: MarqueePointerState = defaultPointerState): void {
        if (isSamePointerState(pointerStateRef.current, state)) {
            return;
        }

        updatePointerState(state);
    }

    function getRowDirection(row: HTMLElement): MarqueeRowDirection {
        return row.classList.contains('category-marquee__row--reverse') ? 'reverse' : 'forward';
    }

    function getPointerStateForRow(
        row: HTMLElement,
        interaction: MarqueeRowInteraction,
    ): MarqueePointerState {
        const rowDirection = getRowDirection(row);

        return {
            forward: rowDirection === 'forward' ? interaction : null,
            reverse: rowDirection === 'reverse' ? interaction : null,
        };
    }

    function getHoveredRowAtPosition(
        section: HTMLElement,
        pointerY: number,
        target: Element | null = null,
    ): HTMLElement | null {
        const row = target?.closest<HTMLElement>('.category-marquee__row');

        if (row) {
            return row;
        }

        const rows = section.querySelectorAll<HTMLElement>('.category-marquee__row');

        return (
            Array.from(rows).find((row) => {
                const bounds = row.getBoundingClientRect();

                return pointerY >= bounds.top && pointerY <= bounds.bottom;
            }) ?? null
        );
    }

    function getPointerStateAtPosition(
        section: HTMLElement,
        pointerPosition: MarqueePointerPosition,
        target: Element | null = null,
    ): MarqueePointerState {
        if (
            window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
            window.matchMedia('(max-width: 48em)').matches
        ) {
            return defaultPointerState;
        }

        const bounds = section.getBoundingClientRect();
        const pointerX = pointerPosition.x - bounds.left;
        const edgeWidth = bounds.width * 0.08;
        const currentTarget =
            target ?? document.elementFromPoint(pointerPosition.x, pointerPosition.y);
        const hoveredRow = getHoveredRowAtPosition(section, pointerPosition.y, currentTarget);

        if (!hoveredRow) {
            return defaultPointerState;
        }

        if (pointerX <= edgeWidth) {
            return getPointerStateForRow(hoveredRow, 'left');
        }

        if (pointerX >= bounds.width - edgeWidth) {
            return getPointerStateForRow(hoveredRow, 'right');
        }

        if (currentTarget?.closest('.category-marquee__pill')) {
            return getPointerStateForRow(hoveredRow, 'pause');
        }

        return defaultPointerState;
    }

    function startPointerMonitor(): void {
        if (monitorTimerRef.current !== null) {
            return;
        }

        monitorTimerRef.current = window.setInterval(() => {
            const section = sectionRef.current;
            const pointerPosition = pointerPositionRef.current;

            if (!section || !pointerPosition) {
                return;
            }

            queuePointerState(getPointerStateAtPosition(section, pointerPosition));
        }, 120);
    }

    function stopPointerMonitor(): void {
        if (monitorTimerRef.current === null) {
            return;
        }

        window.clearInterval(monitorTimerRef.current);
        monitorTimerRef.current = null;
    }

    function handlePointerMove(event: JSX.TargetedPointerEvent<HTMLElement>): void {
        pointerPositionRef.current = {
            x: event.clientX,
            y: event.clientY,
        };
        startPointerMonitor();

        queuePointerState(
            getPointerStateAtPosition(
                event.currentTarget,
                pointerPositionRef.current,
                event.target instanceof Element ? event.target : null,
            ),
        );
    }

    function handlePointerLeave(): void {
        pointerPositionRef.current = null;
        stopPointerMonitor();
        updatePointerState();
    }

    if (categories.length === 0) {
        return null;
    }

    const rows = createCategoryRows(categories);

    return (
        <section
            ref={sectionRef}
            class="category-marquee"
            aria-label={title}
            onPointerMove={handlePointerMove}
            onPointerLeave={handlePointerLeave}
        >
            <div class="category-marquee__viewport">
                {rows.map((row) => {
                    const repeatCount = getRepeatCount(row.categories);

                    return (
                        <div
                            class={`category-marquee__row category-marquee__row--${row.direction}`}
                            key={row.direction}
                        >
                            {Array.from({ length: repeatCount }).map((_, repeatIndex) => (
                                <div
                                    class="category-marquee__set"
                                    aria-hidden={repeatIndex > 0 ? 'true' : undefined}
                                    key={repeatIndex}
                                >
                                    {row.categories.map((category) => (
                                        <CategoryPill
                                            category={category}
                                            inert={repeatIndex > 0}
                                            key={`${category.id}-${repeatIndex}`}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
